import { 
    PitchData, ResultData, BattingStats, PitchResult, 
    HitData, HitReprocessed, BattedBallType, PitchReprocessed 
}  from '../enums/index.js';
import {
    hitClassifier as defaultHitClassifier,
    hardHitClassifier as defaultHardHitClassifier,
    homeRunClassifier as defaultHomeRunClassifier,
    battedBallEventClassifier as defaultBattedBallEventClassifier,
} from '../classificators/index.js';
import { PitchResultToBaseNumberConverter } from '../converters/index.js';
import { ResultPair } from '../valueObjects/index.js';

/**
 * Calculates basic stats for hitting
 */
class BattingStatsCalculator
{
    constructor(pitchResultToBaseNrConverter, hitClassifier, hardHitClassifier, homeRunClassifier, battedBallEventClassifier)
    {
        if (typeof(pitchResultToBaseNrConverter) !== 'undefined' && typeof(pitchResultToBaseNrConverter) !== 'function') {
            throw new TypeError(`pitchResultToBaseNrConverter must be an function or undefined to use default implementation`);
        }
        this.pitchResultToBaseNrConverter = pitchResultToBaseNrConverter || PitchResultToBaseNumberConverter;

        if (typeof(hitClassifier) !== 'undefined' && typeof(hitClassifier) !== 'function') {
            throw new TypeError(`hitClassifier must be an function or undefined to use default implementation`);
        }
        this.hitClassifier = hitClassifier || defaultHitClassifier;

        if (typeof(hardHitClassifier) !== 'undefined' && typeof(hardHitClassifier) !== 'function') {
            throw new TypeError(`hardHitClassifier must be an function or undefined to use default implementation`);
        }
        this.hardHitClassifier = hardHitClassifier || defaultHardHitClassifier;

        if (typeof(homeRunClassifier) !== 'undefined' && typeof(homeRunClassifier) !== 'function') {
            throw new TypeError(`homeRunClassifier must be an function or undefined to use default implementation`);
        }
        this.homeRunClassifier = homeRunClassifier || defaultHomeRunClassifier;

        if (typeof(battedBallEventClassifier) !== 'undefined' && typeof(battedBallEventClassifier) !== 'function') {
            throw new TypeError(`battedBallEventClassifier must be an function or undefined to use default implementation`);
        }
        this.battedBallEventClassifier = battedBallEventClassifier || defaultBattedBallEventClassifier;
    }

    calculateHitterStats(resultPairs, includePercentages) {
        if (!(resultPairs instanceof Array)) {
            throw new TypeError('results must be an instance of array');
        }

        if (typeof(includePercentages) === 'undefined') {
            includePercentages = true;
        }
        else if(typeof(includePercentages) !== 'boolean') {
            throw new TypeError('includePercentages must be an boolean type');
        }

        const battingStats = this.getEmptyStatsStructure();
        const battersPa = [];

        //let's go through results
        for (let i = 0; i < resultPairs.length; i++) {
            const resultPair = resultPairs[i];
            if (!(resultPair instanceof ResultPair)) {
                throw new TypeError(`Item at index ${i} is not an instance of ResultPair`);
            }

            const {pitch, hit} = resultPair;

            const batterSessionPaId = this.getBatterId(pitch, hit);

            if (pitch) {
                const pitchData = pitch[ResultData.Data.key];
                if (typeof(pitchData) !== 'object' || pitchData === null) {
                    throw new TypeError(`pitch.Data at index ${i} is not an object or null`);
                }

                const pitchResult = pitchData[PitchData.Result.key];
                battingStats[BattingStats.NumberOfPitches.key]++;

                const totalBases = this.pitchResultToBaseNrConverter(pitchResult);
                battingStats[BattingStats.TotalBases.key] += totalBases;

                if (this.hitClassifier(pitchResult)) {
                    this.applyHitStats(battingStats, totalBases, hit);
                }
    
                if (this.homeRunClassifier(pitchResult)) {
                    battingStats[BattingStats.HomeRuns.key]++;
                }
    
                if (this.battedBallEventClassifier(pitchResult)) {
                    this.applyFairBallStats(battingStats, hit);
                    battingStats[BattingStats.BattedBalls.key]++;
                }
            }
            else if(!hit) {
                throw new TypeError(`No pitch nor hit at index ${i}`);
            }

            // TODO - better plate appearance check
            if (battersPa.indexOf(batterSessionPaId) < 0) {
                battersPa.push(batterSessionPaId);
            }
        }

        battingStats[BattingStats.PlateAppearances.key] = battersPa.length;

        // Hard Hit % as Hard Hits/Batted Ball Events (BBE), @see https://www.vivaelbirdos.com/2021/4/16/22379521/a-better-approach-to-hard-hit-percentage-and-early-cardinal-results
        if (battingStats.MeasuredFairBalls) {
            battingStats[BattingStats.HardHitRate.key] = 100 * battingStats.HardHits / battingStats.MeasuredFairBalls;
        }

        // TODO - not sure if we should use FairBalls here ?
        if (battingStats.MeasuredFairBalls) {
            // http://m.mlb.com/glossary/advanced-stats/ground-ball-rate
            battingStats[BattingStats.GroundBallRate.key] = 100 * battingStats.GroundBalls / battingStats.MeasuredFairBalls;

            // http://m.mlb.com/glossary/advanced-stats/pop-up-rate
            battingStats[BattingStats.PopUpRate.key] = 100 * battingStats.PopUps / battingStats.MeasuredFairBalls;

            // http://m.mlb.com/glossary/advanced-stats/line-drive-rate
            battingStats[BattingStats.LineDriveRate.key] = 100 * battingStats.LineDrives / battingStats.MeasuredFairBalls;

            // https://www.baseball-reference.com/bullpen/Fly_ball
            // http://m.mlb.com/glossary/advanced-stats/fly-ball-rate
            battingStats[BattingStats.FlyBallRate.key] = 100 * battingStats.FlyBalls / battingStats.MeasuredFairBalls;
        }

        // http://m.mlb.com/glossary/advanced-stats/pitches-per-plate-appearance
        if (battingStats[BattingStats.PlateAppearances.key]) {
            battingStats[BattingStats.PitchesPerPa.key] = battingStats[BattingStats.NumberOfPitches.key] / battingStats[BattingStats.PlateAppearances.key];
        }

        return battingStats;
    }

    getBatterId(pitchResult, hitResult) {
        if (pitchResult) {
            return `${pitchResult[ResultData.SessionId.key]}-${pitchResult[ResultData.BatterTagId.key]}-${pitchResult[ResultData.Data.key][PitchReprocessed.PlateAppearanceR.key]}`;
        }
        else if (hitResult) {
            return `${hitResult[ResultData.SessionId.key]}-${hitResult[ResultData.TagId.key]}-${hitResult[ResultData.Data.key][HitReprocessed.PlateAppearanceR.key]}`;
        }

        return null;
    }

    getEmptyStatsStructure() {
        return {
            [BattingStats.NumberOfPitches.key]: 0,
            [BattingStats.PlateAppearances.key]: 0,
            [BattingStats.Hits.key]: 0,
            [BattingStats.BattedBalls.key]: 0,
            [BattingStats.TotalBases.key]: 0,
            [BattingStats.BaseHits.key]: 0,
            [BattingStats.TwoBaseHits.key]: 0,
            [BattingStats.ThreeBaseHits.key]: 0,
            [BattingStats.HomeRuns.key]: 0,
            [BattingStats.HardHitRate.key]: NaN,
            [BattingStats.LineDriveRate.key]: NaN,
            [BattingStats.GroundBallRate.key]: NaN,
            [BattingStats.PopUpRate.key]: NaN,
            [BattingStats.FlyBallRate.key]: NaN,
            [BattingStats.PitchesPerPa.key]: NaN,
            HardHits: 0,
            FairBalls: 0, // TODO - fair balls are actually equal to BattedBalls
            GroundBalls: 0,
            FlyBalls: 0,
            PopUps: 0,
            LineDrives: 0,
            MeasuredFairBalls: 0,
            MeasuredHits: 0,
        };
    }

    applyHitStats(battingStats, bases, hit) {
        battingStats[BattingStats.Hits.key]++;

        // TODO - maybe classifiers here ?
        switch (bases) {
            case 1:
                battingStats[BattingStats.BaseHits.key]++;
                break;
            case 2:
                battingStats[BattingStats.TwoBaseHits.key]++;
                break;
            case 3:
                battingStats[BattingStats.ThreeBaseHits.key]++;
                break;
            default:
                break;
        }

        // TODO - how about BBE ?
        if (hit && hit[ResultData.Data.key] && hit[ResultData.Data.key][HitData.ExitSpeed.key]) {
            if (this.hardHitClassifier(hit[ResultData.Data.key][HitData.ExitSpeed.key]) ){
                battingStats.HardHits++;
            }

            battingStats.MeasuredHits++;
        }
    }

    applyFairBallStats(battingStats, hit) {
        battingStats.FairBalls++;

        if (hit && hit[ResultData.Data.key]) {
            battingStats.MeasuredFairBalls++;

            const battedBallType = hit[ResultData.Data.key][HitReprocessed.BattedBallTypeR.key];
            switch (battedBallType) {
                case BattedBallType.GroundBall.key:
                    battingStats.GroundBalls++;
                    break;
                case BattedBallType.LineDrive.key:
                    battingStats.LineDrives++;
                    break;
                case BattedBallType.PopUp.key:
                    battingStats.PopUps++;
                    break;
                case BattedBallType.FlyBall.key:
                    battingStats.FlyBalls++;
                    break;
                default:
                    battingStats.MeasuredFairBalls--;
                    break;
            }
        }
    }
}

export default BattingStatsCalculator;